// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.

import { err, ok, type Result } from "neverthrow";
import { proxyErrorMessage } from "~/lib/error-handling";

// Never carries the raw token — only the visible prefix stored alongside the hash.
export interface TokenSummary {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
}

export interface CreateTokenInput {
  name: string;
  // ISO-8601; absence means the server applies its 1-year cap.
  expiresAt?: string;
}

// rawToken appears only here; the server stores SHA256(token), not the token.
export interface CreateTokenResponse {
  id: string;
  name: string;
  prefix: string;
  rawToken: string;
  createdAt: string;
  expiresAt?: string;
}

export interface TokensService {
  listTokens(): Promise<Result<TokenSummary[], Error>>;
  createToken(input: CreateTokenInput): Promise<Result<CreateTokenResponse, Error>>;
  revokeToken(id: string): Promise<Result<void, Error>>;
}

interface TokensServiceConfig {
  authReady: () => boolean;
  authToken: () => string | undefined;
}

interface WireTokenSummary {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
  revoked_at?: string;
}

interface WireCreateTokenResponse {
  id: string;
  name: string;
  prefix: string;
  raw_token: string;
  created_at: string;
  expires_at?: string;
}

interface WireCreateTokenRequest {
  name: string;
  expires_at?: string;
}

const toSummary = (w: WireTokenSummary): TokenSummary => ({
  id: w.id,
  name: w.name,
  prefix: w.prefix,
  createdAt: w.created_at,
  lastUsedAt: w.last_used_at,
  expiresAt: w.expires_at,
  revokedAt: w.revoked_at,
});

const toCreateResponse = (w: WireCreateTokenResponse): CreateTokenResponse => ({
  id: w.id,
  name: w.name,
  prefix: w.prefix,
  rawToken: w.raw_token,
  createdAt: w.created_at,
  expiresAt: w.expires_at,
});

export function createTokensService(config: TokensServiceConfig): TokensService {
  const getHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const token = config.authToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  const request = async <T>(method: string, path: string, body?: unknown): Promise<Result<T, Error>> => {
    if (!config.authReady()) return err(new Error("Auth not ready"));
    try {
      const response = await fetch(`/api${path}`, {
        method,
        headers: getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      });
      if (response.status === 204) return ok(undefined as T);
      if (!response.ok) {
        return err(new Error(proxyErrorMessage(await response.json().catch(() => ({}))) ?? `HTTP ${response.status}`));
      }
      const data: unknown = await response.json();
      return ok(data as T);
    } catch (error) {
      return err(error instanceof Error ? error : new Error(String(error)));
    }
  };

  return {
    async listTokens() {
      const result = await request<{ tokens: WireTokenSummary[] }>("GET", "/tokens");
      return result.map((res) => res.tokens.map(toSummary));
    },
    async createToken(input) {
      const payload: WireCreateTokenRequest = { name: input.name };
      if (input.expiresAt) payload.expires_at = input.expiresAt;
      const result = await request<WireCreateTokenResponse>("POST", "/tokens", payload);
      return result.map(toCreateResponse);
    },
    async revokeToken(id) {
      return request<void>("DELETE", `/tokens/${id}`);
    },
  };
}
